import React, { useState, useEffect } from 'react';
import ApiClient from '../utils/ApiClient';

function LocalInventoryReport() {
    const [inventoryByLocation, setInventoryByLocation] = useState([]);
    const [adjustments, setAdjustments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedView, setSelectedView] = useState('location'); // 'location' or 'adjustments'
    const [skuFilter, setSkuFilter] = useState('');
    const [expandedLocations, setExpandedLocations] = useState({});
    const [locationDetails, setLocationDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [identifierSearch, setIdentifierSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [locationInventory, setLocationInventory] = useState([]);
    const [locationInventoryLoading, setLocationInventoryLoading] = useState(false);

    // Fetch inventory by location on component mount
    useEffect(() => {
        fetchInventoryByLocation();
        fetchLocations();
    }, []);

    // Fetch adjustments when view changes to adjustments
    useEffect(() => {
        if (selectedView === 'adjustments') {
            fetchAdjustments();
        }
    }, [selectedView]);

    const fetchInventoryByLocation = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiClient.post("/api/localinventoryreport");
            if (response?.data) {
                setInventoryByLocation(response.data);
            }
        } catch (error) {
            console.error("Error fetching inventory report:", error);
            setError("Failed to load inventory report");
        } finally {
            setLoading(false);
        }
    };

    const fetchAdjustments = async (sku = '') => {
        setAdjustmentsLoading(true);
        setError(null);
        try {
            const response = await ApiClient.post("/api/inventoryadjustments", {
                sku: sku
            });
            if (response?.data) {
                setAdjustments(response.data);
            }
        } catch (error) {
            console.error("Error fetching adjustments:", error);
            setError("Failed to load adjustments");
        } finally {
            setAdjustmentsLoading(false);
        }
    };

    const handleSkuFilterSubmit = (e) => {
        e.preventDefault();
        fetchAdjustments(skuFilter);
    };

    const handleIdentifierSearch = async (e) => {
        e.preventDefault();
        if (!identifierSearch.trim()) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        setError(null);
        try {
            const response = await ApiClient.post("/api/searchbyidentifier", {
                identifier: identifierSearch.trim()
            });
            if (response?.data) {
                setSearchResults(response.data);
            }
        } catch (error) {
            console.error("Error searching by identifier:", error);
            setError("Failed to search inventory");
        } finally {
            setSearchLoading(false);
        }
    };

    const fetchLocations = async () => {
        try {
            const response = await ApiClient.get("/api/locations");
            if (response?.data) {
                setLocations(response.data);
            }
        } catch (error) {
            console.error("Error fetching locations:", error);
        }
    };

    const handleLocationChange = async (e) => {
        const location = e.target.value;
        setSelectedLocation(location);

        if (!location) {
            setLocationInventory([]);
            return;
        }

        setLocationInventoryLoading(true);
        setError(null);
        try {
            const response = await ApiClient.post("/api/locationinventorypricelist", {
                location: location
            });
            if (response?.data) {
                setLocationInventory(response.data);
            }
        } catch (error) {
            console.error("Error fetching location inventory:", error);
            setError(`Failed to load inventory for location ${location}`);
        } finally {
            setLocationInventoryLoading(false);
        }
    };

    const getTotalItems = () => {
        return inventoryByLocation.reduce((total, location) => total + (location.item_count || 0), 0);
    };

    const toggleLocationExpanded = async (location) => {
        const willBeExpanded = !expandedLocations[location];
        setExpandedLocations(prev => ({ ...prev, [location]: willBeExpanded }));
        
        if (willBeExpanded && !locationDetails[location]) {
            setLoadingDetails(prev => ({ ...prev, [location]: true }));
            setError(null);
            
            try {
                const response = await ApiClient.post("/api/localinventorydetails", {
                    location: location
                });
                if (response?.data) {
                    setLocationDetails(prev => ({ ...prev, [location]: response.data }));
                }
            } catch (error) {
                console.error("Error fetching location details:", error);
                setError(`Failed to load details for location ${location}`);
                setExpandedLocations(prev => ({ ...prev, [location]: false }));
            } finally {
                setLoadingDetails(prev => ({ ...prev, [location]: false }));
            }
        }
    };

    const getAdjustmentTypeColor = (type) => {
        switch (type) {
            case 'ADD':
                return 'bg-green-100 text-green-800';
            case 'LOCATION_CHANGE':
                return 'bg-blue-100 text-blue-800';
            case 'AUDIT_COUNT':
                return 'bg-yellow-100 text-yellow-800';
            case 'REMOVE':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="space-y-8 px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">Local Inventory Report</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        View inventory distribution by location and track adjustment history.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                    <div className="inline-flex rounded-md shadow-sm" role="group">
                        <button
                            type="button"
                            onClick={() => setSelectedView('location')}
                            className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-l-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 ${
                                selectedView === 'location' ? 'bg-blue-700 text-white' : 'bg-white text-gray-900'
                            }`}
                        >
                            By Location
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedView('adjustments')}
                            className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-r-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 ${
                                selectedView === 'adjustments' ? 'bg-blue-700 text-white' : 'bg-white text-gray-900'
                            }`}
                        >
                            Adjustments
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                    </div>
                </div>
            )}

            {/* Location View */}
            {selectedView === 'location' && (
                <div>
                    {/* Search Bar */}
                    <div className="bg-white shadow rounded-lg mb-6">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Search by Identifier</h3>
                            <form onSubmit={handleIdentifierSearch} className="flex space-x-4">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="Enter identifier (e.g. X002ENS2WP)"
                                        value={identifierSearch}
                                        onChange={(e) => setIdentifierSearch(e.target.value)}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    disabled={searchLoading}
                                >
                                    {searchLoading ? 'Searching...' : 'Search'}
                                </button>
                            </form>
                            
                            {/* Search Results */}
                            {searchResults.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Search Results</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Identifier</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ASIN</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {searchResults.map((result, idx) => (
                                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                                                {result.location}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-900">{result.identifier}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-900">{result.asin || '-'}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-900">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                {result.count}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Location Dropdown and Inventory Card */}
                    <div className="bg-white shadow rounded-lg mb-6">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">View Inventory by Location</h3>
                            <div className="flex flex-col space-y-4">
                                <div>
                                    <label htmlFor="location-select" className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Location
                                    </label>
                                    <select
                                        id="location-select"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        value={selectedLocation}
                                        onChange={handleLocationChange}
                                    >
                                        <option value="">-- Select a Location --</option>
                                        {locations.map((loc, idx) => (
                                            <option key={idx} value={loc.location}>
                                                {loc.location}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Location Inventory Card */}
                                {selectedLocation && (
                                    <div className="mt-4">
                                        {locationInventoryLoading ? (
                                            <div className="text-center py-4">
                                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                                <p className="mt-2 text-sm text-gray-500">Loading inventory...</p>
                                            </div>
                                        ) : (
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h4 className="text-md font-semibold text-gray-900">
                                                        Inventory in {selectedLocation}
                                                    </h4>
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                                        {locationInventory.length} items
                                                    </span>
                                                </div>

                                                {locationInventory.length === 0 ? (
                                                    <p className="text-sm text-gray-500 text-center py-4">No inventory found in this location</p>
                                                ) : (
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-300">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Code</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product Title</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Identifier</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ASIN</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {locationInventory.map((item, idx) => (
                                                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                                        <td className="px-4 py-2 text-sm font-medium text-gray-700">
                                                                            {item.id}
                                                                        </td>
                                                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                                                            {item.item_code || '-'}
                                                                        </td>
                                                                        <td className="px-4 py-2 text-sm text-gray-900">
                                                                            {item.product_title || '-'}
                                                                        </td>
                                                                        <td className="px-4 py-2 text-sm text-gray-700">
                                                                            {item.identifier || '-'}
                                                                        </td>
                                                                        <td className="px-4 py-2 text-sm text-gray-700">
                                                                            {item.asin || '-'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Items</dt>
                                            <dd className="text-lg font-medium text-gray-900">{getTotalItems()}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Locations</dt>
                                            <dd className="text-lg font-medium text-gray-900">{inventoryByLocation.length}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <button
                                            onClick={fetchInventoryByLocation}
                                            disabled={loading}
                                            className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                                        >
                                            <svg className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Refresh Data</dt>
                                            <dd className="text-sm font-medium text-gray-900">Click to update</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inventory Table */}
                    <div className="bg-white shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Inventory by Location</h3>
                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                    <p className="mt-2 text-sm text-gray-500">Loading inventory data...</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Count</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {inventoryByLocation.length === 0 ? (
                                                <tr>
                                                    <td colSpan="2" className="px-6 py-4 text-sm text-gray-500 text-center">
                                                        No inventory data found
                                                    </td>
                                                </tr>
                                            ) : (
                                                inventoryByLocation.map((location, index) => (
                                                    <React.Fragment key={index}>
                                                        <tr 
                                                            className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer hover:bg-gray-100`}
                                                            onClick={() => toggleLocationExpanded(location.location)}
                                                        >
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                <div className="flex items-center">
                                                                    <span className="mr-2 text-gray-400">
                                                                        {expandedLocations[location.location] ? '▼' : '▶'}
                                                                    </span>
                                                                    {location.location}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                    {location.item_count} items
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        {expandedLocations[location.location] && (
                                                            <tr className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                <td colSpan="2" className="px-6 py-4">
                                                                    {loadingDetails[location.location] ? (
                                                                        <div className="text-center py-4">
                                                                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                                                            <p className="mt-2 text-sm text-gray-500">Loading details...</p>
                                                                        </div>
                                                                    ) : locationDetails[location.location] ? (
                                                                        <div className="bg-white shadow rounded-lg p-4">
                                                                            <h4 className="text-sm font-medium text-gray-900 mb-3">Items in {location.location}</h4>
                                                                            <div className="overflow-x-auto">
                                                                                <table className="min-w-full divide-y divide-gray-200">
                                                                                    <thead className="bg-gray-50">
                                                                                        <tr>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Seller SKU</th>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Identifier</th>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ASIN</th>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mfr SKU</th>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product Title</th>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                                        {locationDetails[location.location].length === 0 ? (
                                                                                            <tr>
                                                                                                <td colSpan="7" className="px-4 py-4 text-sm text-gray-500 text-center">
                                                                                                    No items found in this location
                                                                                                </td>
                                                                                            </tr>
                                                                                        ) : (
                                                                                            locationDetails[location.location].map((item, itemIndex) => (
                                                                                                <tr key={itemIndex} className={itemIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.seller_sku || '-'}</td>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.identifier || '-'}</td>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.asin || '-'}</td>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.item_code || '-'}</td>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-900">{item.product_title || '-'}</td>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-900">
                                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                                                            {item.count}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                                                                                                        {item.last_updated ? new Date(item.last_updated).toLocaleString() : '-'}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))
                                                                                        )}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    ) : null}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Adjustments View */}
            {selectedView === 'adjustments' && (
                <div>
                    {/* SKU Filter */}
                    <div className="bg-white shadow rounded-lg mb-6">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Filter Adjustments</h3>
                            <form onSubmit={handleSkuFilterSubmit} className="flex space-x-4">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="Enter SKU to filter (leave empty for all)"
                                        value={skuFilter}
                                        onChange={(e) => setSkuFilter(e.target.value)}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    disabled={adjustmentsLoading}
                                >
                                    {adjustmentsLoading ? 'Loading...' : 'Filter'}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Adjustments Table */}
                    <div className="bg-white shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Inventory Adjustments</h3>
                            {adjustmentsLoading ? (
                                <div className="text-center py-4">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                    <p className="mt-2 text-sm text-gray-500">Loading adjustments...</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {adjustments.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-4 text-sm text-gray-500 text-center">
                                                        No adjustments found
                                                    </td>
                                                </tr>
                                            ) : (
                                                adjustments.map((adjustment, index) => (
                                                    <tr key={adjustment.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {adjustment.sku}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAdjustmentTypeColor(adjustment.type)}`}>
                                                                {adjustment.type}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {adjustment.quantity}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-900">
                                                            <div className="max-w-xs truncate" title={adjustment.reason}>
                                                                {adjustment.reason}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {adjustment.created_at ? new Date(adjustment.created_at).toLocaleString() : ''}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LocalInventoryReport;